import { gql } from '@apollo/client';
import { useMutation } from 'platform/useMutation';
import { useState, useEffect, useCallback } from 'react';
import { useObservability } from 'shared/helpers/observability';

import {
  MarkOfferViewed,
  MarkOfferViewedVariables,
} from 'platform/graphql/__generated__/MarkOfferViewed';
import { useQualificationContext } from 'shared/helpers/QualificationContext';
import { useOfferContext } from 'sellDirect/OfferContext';

export const MARK_OFFER_VIEWED = gql`
  mutation MarkOfferViewed($sellerLeadUUID: String!) {
    seller {
      markBasketAsViewed(sellerLeadUuid: $sellerLeadUUID)
    }
  }
`;

/**
 * Hook to use to mark Sell Direct Offers as viewed.
 *
 * By default, it will triggers the mutation on initial render.
 * Otherwise, you can pass `triggerOnFirstRender: false` to prevent that and use the `markOfferViewed` function.
 *
 * Warning: This hook must be mounted within:
 *    1. Qualification Context
 *    2. Offer Context
 */
export const useMarkOfferAsViewed = (
  { triggerOnFirstRender }: { triggerOnFirstRender: boolean } = { triggerOnFirstRender: true },
) => {
  const { trackError } = useObservability();
  const { lead } = useQualificationContext();
  const { offer } = useOfferContext();
  const [offerViewed, setOfferViewed] = useState(false);

  const [markOfferViewedMutation] = useMutation<MarkOfferViewed, MarkOfferViewedVariables>(
    MARK_OFFER_VIEWED,
    { fetchPolicy: 'no-cache' },
  );

  /** Given a Seller Lead UUID, it marks the offer for that lead as viewed */
  const markOfferViewed = useCallback(
    (sellerLeadUUID: string) => {
      if (!offerViewed) {
        markOfferViewedMutation({ variables: { sellerLeadUUID } }).catch((error) =>
          trackError('mark-offer-viewed', error, { sellerLeadUUID }),
        );
        setOfferViewed(true);
      }
    },
    // trackError does not maintain its identity across re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [markOfferViewedMutation, setOfferViewed, offerViewed],
  );

  // If triggerOnMount is set to true, it will automatically call the mutation
  useEffect(() => {
    if (triggerOnFirstRender && !offerViewed && lead?.id && !offer?.viewedAt) {
      markOfferViewed(lead.id);
    }
  }, [triggerOnFirstRender, offerViewed, lead?.id, offer?.viewedAt, markOfferViewed]);

  return { markOfferViewed };
};
